import { createSignal, For } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useRefineStore } from "../../store/RefineProvider";
import { RefineEditDialog } from "../RefineEditDialog";
import { RefineStepCard } from "../RefineStepCard";

export function SkillsStep() {
  const { t } = useI18n();
  const { state, updateFields } = useRefineStore();
  const [editOpen, setEditOpen] = createSignal(false);
  const [editSkills, setEditSkills] = createSignal<string[]>([]);
  const [newSkill, setNewSkill] = createSignal("");

  const handleOpenEdit = () => {
    setEditSkills([...state.skills]);
    setNewSkill("");
    setEditOpen(true);
  };

  const handleAddSkill = () => {
    const skill = newSkill().trim();
    if (skill && !editSkills().includes(skill)) {
      setEditSkills((prev) => [...prev, skill]);
      setNewSkill("");
    }
  };

  const handleRemoveSkill = (skill: string) => {
    setEditSkills((prev) => prev.filter((s) => s !== skill));
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddSkill();
    }
  };

  const handleSave = () => {
    updateFields({ skills: editSkills() });
    setEditOpen(false);
  };

  return (
    <>
      <RefineStepCard
        title={t("jobOffers.refine.steps.skills")}
        onEdit={handleOpenEdit}
      >
        <div class="flex flex-wrap gap-2">
          <For
            each={state.skills}
            fallback={<p class="text-sm text-muted-foreground">—</p>}
          >
            {(skill) => <Badge variant="secondary">{skill}</Badge>}
          </For>
        </div>
      </RefineStepCard>

      <RefineEditDialog
        open={editOpen()}
        onOpenChange={setEditOpen}
        title={t("jobOffers.refine.editSkills")}
      >
        <div class="space-y-4">
          <div class="flex gap-2">
            <Input
              value={newSkill()}
              onInput={(e) => setNewSkill(e.currentTarget.value)}
              onKeyDown={handleKeyDown}
              placeholder={t("jobOffers.refine.addSkillPlaceholder")}
            />
            <Button variant="outline" onClick={handleAddSkill}>
              {t("jobOffers.refine.addSkill")}
            </Button>
          </div>

          <div class="flex flex-wrap gap-2">
            <For each={editSkills()}>
              {(skill) => (
                <Badge
                  variant="secondary"
                  class="cursor-pointer hover:bg-destructive/20"
                  onClick={() => handleRemoveSkill(skill)}
                >
                  {skill} &times;
                </Badge>
              )}
            </For>
          </div>

          <div class="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button onClick={handleSave}>{t("common.save")}</Button>
          </div>
        </div>
      </RefineEditDialog>
    </>
  );
}
